import { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { object, string } from "yup";

import { api, apiHooks } from "~/api.js";
import {
  Button,
  FormLabel,
  HelperText,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

const s12ConfigurationSchema = object({
  endpoint: string().min(5).required(),
  username: string().min(3).required(),
  password: string().min(5).required(),
});

function S12ConfigurationForm({
  s12Configuration: { endpoint, username },
  reload,
}: {
  s12Configuration: { endpoint: string; username: string };
  reload: () => void;
}) {
  return (
    <Formik<{
      endpoint: string;
      username: string;
      password: string;
    }>
      initialValues={{ endpoint, username, password: "" }}
      validationSchema={s12ConfigurationSchema}
      onSubmit={async (values) => {
        const result = await api.configuration.updateS12ApprovalConfiguration({
          endpoint: values.endpoint,
          username: values.username,
          password: values.password,
        });

        if (result.status === 204) {
          renderSuccessToast({
            message: "S12 approval configuration updated",
          });
          reload();
        } else {
          renderErrorToast({
            message: "Failed to update S12 approval configuration",
          });
        }
      }}
    >
      {(formikData) => {
        const { values, setValues, errors, touched, submitForm } = formikData;

        return (
          <Box
            sx={{
              maxWidth: "600px",
              marginBottom: "2em",
              gap: "1em",
            }}
          >
            <Box sx={{ mt: "2em" }}>
              <FormLabel
                label={"Endpoint (*)"}
                error={touched.endpoint && !!errors.endpoint}
              />
              <TextField
                name={"endpoint"}
                useFullWidth={true}
                value={values.endpoint}
                onChange={(e) => {
                  setValues({
                    ...values,
                    endpoint: e,
                  });
                }}
              />
              {errors.endpoint && touched.endpoint && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.endpoint as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Username (*)"}
                error={touched.username && !!errors.username}
              />
              <TextField
                name={"username"}
                useFullWidth={true}
                value={values.username}
                onChange={(e) => {
                  setValues({
                    ...values,
                    username: e,
                  });
                }}
              />
              {errors.username && touched.username && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.username as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"Password - OBFUSCATED (*)"}
                error={touched.password && !!errors.password}
              />
              <TextField
                name={"password"}
                useFullWidth={true}
                value={values.password}
                onChange={(e) => {
                  setValues({
                    ...values,
                    password: e,
                  });
                }}
              />
              {errors.password && touched.password && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.password as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <Button onClick={submitForm} label="Submit" type="submit" />
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
}

function S12ApprovalChecker() {
  const [gmcNumber, setGmcNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    type: "success" | "not-found" | "error";
    surname?: string;
    section12ExpiryDate?: string;
    message?: string;
  } | null>(null);

  const handleCheck = async () => {
    if (!gmcNumber.trim()) {
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await api.system.checkS12Approval(gmcNumber.trim());

      if (response.status === 200) {
        setResult({
          type: "success",
          surname: response.data.surname,
          section12ExpiryDate: response.data.section12ExpiryDate,
        });
      } else if (response.status === 404) {
        setResult({
          type: "not-found",
          message: `GMC number ${gmcNumber} not found in S12 register`,
        });
      } else {
        setResult({
          type: "error",
          message: "Unexpected response from S12 approval service",
        });
      }
    } catch {
      setResult({
        type: "error",
        message: "Failed to check S12 approval status",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: "600px" }}>
      <Box sx={{ display: "flex", gap: "1em", alignItems: "center" }}>
        <Box sx={{ flex: 1 }}>
          <FormLabel label="GMC Number" />
          <TextField
            name="gmcNumber"
            useFullWidth={true}
            value={gmcNumber}
            onChange={(e) => {
              setGmcNumber(e);
            }}
          />
        </Box>
        <Box sx={{ mt: "1.5em" }}>
          <Button
            onClick={() => {
              void handleCheck();
            }}
            label={loading ? "Checking..." : "Check"}
            disabled={loading || !gmcNumber.trim()}
          />
        </Box>
      </Box>

      {result?.type === "success" && (
        <Alert severity="success" sx={{ mt: "1em" }}>
          <strong>S12 Approved</strong>
          <br />
          Surname: {result.surname}
          <br />
          Expires:{" "}
          {new Date(result.section12ExpiryDate!).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
          <br />
          <a
            href={`https://www.gmc-uk.org/registrants/${gmcNumber.trim()}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on GMC register
          </a>
        </Alert>
      )}

      {result?.type === "not-found" && (
        <Alert severity="warning" sx={{ mt: "1em" }}>
          {result.message}
        </Alert>
      )}

      {result?.type === "error" && (
        <Alert severity="error" sx={{ mt: "1em" }}>
          {result.message}
        </Alert>
      )}
    </Box>
  );
}

export function S12ApprovalConfiguration() {
  const [{ data: s12Configuration, loading: s12ConfigurationLoading }, reload] =
    apiHooks.configuration.getS12ApprovalConfiguration();

  return (
    <Box sx={{ mt: "2em" }}>
      <Typography variant="h5">S12 Approval Configuration</Typography>
      {s12ConfigurationLoading ? (
        <CircularProgress />
      ) : (
        <S12ConfigurationForm
          s12Configuration={s12Configuration!}
          reload={reload}
        />
      )}

      <Divider sx={{ my: "2em" }} />

      <Typography variant="h5">S12 Approval Checker</Typography>
      <Typography variant="body2" sx={{ mt: "0.5em", mb: "1em" }}>
        Enter a GMC number to check whether a doctor is S12 approved.
      </Typography>
      <S12ApprovalChecker />
    </Box>
  );
}
